import * as React from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import {
  LayoutDashboard, Users, ShieldCheck, Settings as SettingsIcon,
  Activity, ChevronsUpDown, LogOut, Plus, Check, Search,
  CalendarClock, MessageSquare, Columns3, ClipboardList,
  UserCog, Wallet, BookOpen, MapPin, Sparkles, ShieldAlert, Grid3x3,
  Building2, Inbox, FolderOpen, Sun, Moon, Monitor,
} from "lucide-react";
import { useTheme } from "next-themes";
import { useAuth } from "@/context/AuthContext";
import { useDensity } from "@/hooks/useDensity";
import { useCompany } from "@/context/CompanyContext";
import { useCommsUnread } from "@/context/CommsUnreadContext";
import { useTerminology } from "@/hooks/useTerminology";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const MODULE_ICONS = {
  bookings: CalendarClock,
  comms:    MessageSquare,
  boards:   Columns3,
  rotas:    ClipboardList,
  hr:       UserCog,
  pay:      Wallet,
  books:    BookOpen,
  plus:     Sparkles,
  cases:    FolderOpen,
};

export default function Sidebar({ onOpenPalette, open = false, onClose }) {
  const { companyId } = useParams();
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const { companies, company, entitlements, catalog, has, listCompanies } = useCompany();
  const { total: commsUnread } = useCommsUnread();
  const t = useTerminology();
  const { theme, setTheme } = useTheme();
  const { density, setDensity } = useDensity();
  const [switcherOpen, setSwitcherOpen] = React.useState(false);
  const [userMenuOpen, setUserMenuOpen] = React.useState(false);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [newName, setNewName] = React.useState("");
  const [creating, setCreating] = React.useState(false);

  const closeMobile = React.useCallback(() => { onClose && onClose(); }, [onClose]);

  async function createCompany(e) {
    e.preventDefault();
    if (!newName.trim() || creating) return;
    setCreating(true);
    try {
      const { data } = await api.post("/companies", { name: newName.trim() });
      toast.success(`Created ${data.company.name}`);
      await listCompanies();
      setCreateOpen(false); setSwitcherOpen(false);
      setNewName("");
      nav(`/c/${data.company.id}`);
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2?.response?.data?.detail) || "Could not create");
    } finally { setCreating(false); }
  }

  const modules = catalog?.modules || [];
  const enabled = entitlements?.modules || {};

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-30 bg-black/30 lg:hidden"
          onClick={closeMobile}
          data-testid="sidebar-backdrop"
        />
      )}
      <aside
        className={`w-[240px] shrink-0 h-screen border-r border-separator flex flex-col z-40 fixed inset-y-0 left-0 transition-transform duration-200 will-change-transform ${
          open ? "translate-x-0" : "-translate-x-full"
        } lg:sticky lg:top-0 lg:translate-x-0`}
        style={{
          background: "var(--material-thick)",
          backdropFilter: "var(--material-blur)",
          WebkitBackdropFilter: "var(--material-blur)",
        }}
        data-testid="sidebar"
        data-open={open ? "true" : "false"}
      >
      <div className="px-4 pt-4 pb-3">
        <div className="flex items-center gap-2">
          <div className="h-6 w-6 rounded-md bg-brand-violet flex items-center justify-center text-white font-bold text-[13px]">F</div>
          <div className="font-bold tracking-tight text-[14.5px] text-brand-ink">Fable Ops</div>
        </div>
      </div>

      {/* Company switcher */}
      <div className="relative px-3">
        <button
          type="button"
          onClick={() => setSwitcherOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-2 rounded-md border border-brand-rule bg-card px-2.5 py-2 hover:bg-brand-hush"
          data-testid="company-switcher-trigger"
        >
          <span className="flex items-center gap-2 min-w-0">
            <span className="h-5 w-5 rounded-md bg-brand-violet text-white text-[10px] font-bold flex items-center justify-center shrink-0">
              {company?.name?.charAt(0)?.toUpperCase() || "?"}
            </span>
            <span className="text-[12.5px] font-semibold text-brand-ink truncate">
              {company?.name || "No company"}
            </span>
          </span>
          <ChevronsUpDown size={13} className="text-brand-muted" />
        </button>
        {switcherOpen && (
          <div className="absolute z-30 left-3 right-3 top-full mt-1 bg-card border border-brand-rule rounded-md shadow-card py-1"
               data-testid="company-switcher-menu">
            <div className="max-h-56 overflow-y-auto">
              {companies.map((c) => (
                <button
                  key={c.id}
                  onClick={() => { setSwitcherOpen(false); nav(`/c/${c.id}`); }}
                  data-testid={`company-option-${c.id}`}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-[12.5px] hover:bg-brand-hush"
                >
                  <span className="h-4 w-4 rounded bg-brand-violet text-white text-[9px] font-bold flex items-center justify-center">
                    {c.name.charAt(0).toUpperCase()}
                  </span>
                  <span className="flex-1 truncate">{c.name}</span>
                  {c.id === companyId && <Check size={13} className="text-brand-violet" />}
                </button>
              ))}
            </div>
            <div className="border-t border-brand-rule mt-1 pt-1">
              <button
                onClick={() => { setSwitcherOpen(false); setCreateOpen(true); }}
                data-testid="create-company-trigger"
                className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-[12.5px] text-brand-violet hover:bg-brand-hush"
              >
                <Plus size={13} /> Create company
              </button>
            </div>
          </div>
        )}
      </div>

      {createOpen && (
        <form onSubmit={createCompany} className="px-3 mt-2" data-testid="create-company-form">
          <input
            className="field-input"
            placeholder="Company name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            autoFocus
            data-testid="create-company-name"
          />
          <div className="mt-2 flex gap-2">
            <button type="submit" className="btn-violet flex-1" disabled={creating || !newName.trim()}
                    data-testid="create-company-submit">
              {creating ? "Creating…" : "Create"}
            </button>
            <button type="button" onClick={() => { setCreateOpen(false); setNewName(""); }}
                    className="btn-outline" data-testid="create-company-cancel">
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Search / palette trigger */}
      <div className="px-3 mt-3">
        <button
          type="button"
          onClick={onOpenPalette}
          className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-brand-rule bg-brand-canvas hover:bg-card text-left"
          data-testid="palette-trigger"
        >
          <Search size={13} className="text-brand-muted" />
          <span className="text-[12px] text-brand-muted flex-1">Jump to…</span>
          <span className="mono-num text-[10px] chip">⌘K</span>
        </button>
      </div>

      {/* Nav */}
      <nav className="mt-4 px-2 flex-1 overflow-y-auto">
        <div className="px-2 micro-label mb-1">Workspace</div>
        {has("wall.view") && (
          <NavLink to={`/c/${companyId}/wall`} onClick={closeMobile} className={({isActive}) => `nav-item ${isActive?'active':''}`}
                   data-testid="nav-wall">
            <Grid3x3 size={14} /> Wall
          </NavLink>
        )}
        <NavLink to={`/c/${companyId}`} end onClick={closeMobile} className={({isActive}) => `nav-item ${isActive?'active':''}`}
                 data-testid="nav-dashboard">
          <LayoutDashboard size={14} /> Dashboard
        </NavLink>
        <NavLink to={`/c/${companyId}/members`} onClick={closeMobile} className={({isActive}) => `nav-item ${isActive?'active':''}`}
                 data-testid="nav-members">
          <Users size={14} /> Members
        </NavLink>
        {has("hr.view_all") && (
          <NavLink to={`/c/${companyId}/org`} onClick={closeMobile} className={({isActive}) => `nav-item ${isActive?'active':''}`}
                   data-testid="nav-org">
            <Building2 size={14} /> Organisation
          </NavLink>
        )}
        <NavLink to={`/c/${companyId}/sites`} onClick={closeMobile} className={({isActive}) => `nav-item ${isActive?'active':''}`}
                 data-testid="nav-sites">
          <MapPin size={14} /> {t("sites")}
        </NavLink>
        {has("roles.manage") && (
          <NavLink to={`/c/${companyId}/roles`} onClick={closeMobile} className={({isActive}) => `nav-item ${isActive?'active':''}`}
                   data-testid="nav-roles">
            <ShieldCheck size={14} /> Roles
          </NavLink>
        )}
        <NavLink to={`/c/${companyId}/alerts`} onClick={closeMobile} className={({isActive}) => `nav-item ${isActive?'active':''}`}
                 data-testid="nav-alerts">
          <ShieldAlert size={14} /> Alerts
        </NavLink>
        {(has("hr.manage") || has("rotas.manage") || has("pay.manage") || has("cases.manage") || has("cases.confidential")) && (
          <NavLink to={`/c/${companyId}/queue`} onClick={closeMobile} className={({isActive}) => `nav-item ${isActive?'active':''}`}
                   data-testid="nav-queue">
            <Inbox size={14} /> Queue
          </NavLink>
        )}
        {has("audit.view") && (
          <NavLink to={`/c/${companyId}/audit`} onClick={closeMobile} className={({isActive}) => `nav-item ${isActive?'active':''}`}
                   data-testid="nav-audit">
            <Activity size={14} /> Audit
          </NavLink>
        )}
        {(has("rules.manage") || has("company.manage")) && (
          <NavLink to={`/c/${companyId}/rules`} onClick={closeMobile} className={({isActive}) => `nav-item ${isActive?'active':''}`}
                   data-testid="nav-rules">
            <ShieldCheck size={14} /> Rules
          </NavLink>
        )}
        <NavLink to={`/c/${companyId}/settings`} onClick={closeMobile} className={({isActive}) => `nav-item ${isActive?'active':''}`}
                 data-testid="nav-settings">
          <SettingsIcon size={14} /> Settings
        </NavLink>

        <div className="mt-4 px-2 micro-label mb-1">Modules</div>
        {modules.map((m) => {
          const on = !!enabled[m.id];
          const viewCap = m.view_capability;
          const canView = has(viewCap);
          const clickable = on && canView;
          const Icon = MODULE_ICONS[m.id] || Columns3;
          // Live unread badge for Comms — only surfaced on that module.
          const unread = (m.id === "comms" && clickable) ? (commsUnread || 0) : 0;
          const isPlus = m.id === "plus";
          // Terminology override: rename Bookings label per company noun map.
          const label = m.id === "bookings" ? t("bookings") : m.label;
          return (
            <NavLink
              key={m.id}
              to={clickable ? `/c/${companyId}/m/${m.id}` : "#"}
              onClick={(e) => {
                if (!clickable) { e.preventDefault(); toast.info(
                  !on ? `${m.label} is disabled for this company` :
                        `You don't have ${viewCap}`); return; }
                closeMobile();
              }}
              className={({isActive}) => {
                const active = isActive && clickable;
                if (isPlus && active) return `nav-item teal-active`;
                return `nav-item ${active ? 'active' : ''} ${clickable ? '' : 'disabled'}`;
              }}
              data-testid={`nav-module-${m.id}`}
            >
              <Icon size={14} className={isPlus && clickable ? 'text-brand-teal' : ''} />
              <span className="flex-1">{label}</span>
              {!on && <span className="mono-num text-[9px] chip">off</span>}
              {on && unread > 0 && (
                <span
                  className="mono-num text-[10px] font-semibold rounded-full bg-brand-violet text-white px-1.5 h-4 min-w-[16px] flex items-center justify-center"
                  data-testid={`nav-module-${m.id}-unread`}
                >
                  {unread}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* User menu */}
      <div className="relative border-t border-brand-rule p-2">
        <button
          type="button"
          onClick={() => setUserMenuOpen((v) => !v)}
          className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-brand-hush"
          data-testid="user-menu-trigger"
        >
          <span className="h-7 w-7 rounded-full bg-gradient-to-br from-brand-violet to-brand-violet-hi text-white flex items-center justify-center text-[11px] font-bold">
            {user?.name?.charAt(0)?.toUpperCase() || "?"}
          </span>
          <span className="min-w-0 flex-1 text-left">
            <div className="text-[12px] font-semibold text-brand-ink truncate">{user?.name}</div>
            <div className="text-[10.5px] text-brand-muted truncate">{user?.email}</div>
          </span>
        </button>
        {userMenuOpen && (
          <div className="absolute bottom-full mb-1 left-2 right-2 bg-card border border-separator rounded-md shadow-apple-3 py-1"
               data-testid="user-menu">
            <div className="px-3 pt-1 pb-1 text-footnote text-label-secondary">Appearance</div>
            <div className="px-2 pb-1 flex items-center gap-1" data-testid="theme-switcher">
              {[
                { k: "system", Icon: Monitor, label: "System" },
                { k: "light",  Icon: Sun,     label: "Light"  },
                { k: "dark",   Icon: Moon,    label: "Dark"   },
              ].map(({ k, Icon, label }) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setTheme(k)}
                  className={`flex-1 flex items-center justify-center gap-1 rounded-md py-1.5 text-footnote transition
                    ${theme === k ? "bg-fill-tertiary text-label" : "text-label-secondary hover:bg-fill-quaternary"}`}
                  data-testid={`theme-${k}`}
                  aria-label={`Switch to ${label} theme`}
                >
                  <Icon size={13} /> {label}
                </button>
              ))}
            </div>
            <div className="h-px bg-separator mx-2 my-1" />
            <div className="px-3 pt-1 pb-1 text-footnote text-label-secondary">Density (mouse only)</div>
            <div className="px-2 pb-1 flex items-center gap-1" data-testid="density-switcher">
              {[
                { k: "comfortable", label: "Comfortable" },
                { k: "compact",     label: "Compact"    },
              ].map(({ k, label }) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setDensity(k)}
                  className={`flex-1 flex items-center justify-center gap-1 rounded-md py-1.5 text-footnote transition
                    ${density === k ? "bg-fill-tertiary text-label" : "text-label-secondary hover:bg-fill-quaternary"}`}
                  data-testid={`density-${k}`}
                  aria-label={`Switch to ${label} density`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="h-px bg-separator mx-2 my-1" />
            <button
              onClick={() => { setUserMenuOpen(false); logout().then(()=>nav('/login')); }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-body text-destructive hover:bg-fill-quaternary"
              data-testid="user-menu-logout"
            >
              <LogOut size={13} /> Sign out
            </button>
          </div>
        )}
      </div>
    </aside>
    </>
  );
}
